import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { LooseAutocomplete } from "../pojo/Util";
import { S25LoadingInlineModule } from "../modules/s25-loading/s25.loading.inline.module";
import { S25Util } from "../util/s25-util";

export type ButtonClassSuffix =
    | "primary"
    | "outline"
    | "danger"
    | "danger--outline"
    | "danger--transparent"
    | "flat"
    | "link"
    | "success"
    | "warning"
    | "none";

@Component({
    selector: "s25-ng-button",
    template: `
        <button
            class="aw-button aw-button--{{ type }} {{ buttonClass }}"
            [class.ngAnchor]="type === 'link'"
            [attr.type]="htmlButtonType"
            (click)="clickHandler($event)"
            [disabled]="disabled || isLoading"
            [attr.isLoading]="isLoading"
            [title]="disabled ? (disabledReason ?? '') : title || ''"
            [attr.aria-label]="ariaLabel || title || ''"
            [attr.aria-expanded]="ariaExpanded()"
            [attr.aria-controls]="ariaControls()"
        >
            @if (isLoading) {
                <s25-ng-loading-inline-static [text]="loadingText"></s25-ng-loading-inline-static>
            }
            @if (!isLoading) {
                {{ buttonText }}
                <ng-content></ng-content>
            }
        </button>
    `,
    styles: `
        :host {
            display: inline-flex;
            align-items: center;
        }

        button {
            height: 100%;
            width: 100%;
            text-align: var(--button--text-align, center);
        }

        s25-ng-loading-inline-static {
            position: absolute;
            top: 50%;
            left: 50%;
            translate: -50% -50%;
        }

        ::ng-deep s25-ng-button > button.aw-button--primary > s25-ng-loading-inline-static .c-loading__inner {
            stroke: white;
        }

        ::ng-deep s25-ng-button > button > s25-ng-loading-inline-static .s25-loading {
            background-color: transparent !important;
            padding: 0;
        }

        .aw-button--flat {
            border: none;
            color: currentColor;
        }

        .aw-button--link {
            border: none;
            background: transparent;
            cursor: pointer;
            padding: 0;
        }

        .aw-button--link:hover {
            text-decoration: underline !important;
        }
    `,
    imports: [S25LoadingInlineModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
})
export class S25ButtonComponent<OnClick extends (...args: any[]) => Promise<unknown> | void = () => void>
    implements OnChanges, OnInit
{
    @Input({ required: true }) type: LooseAutocomplete<ButtonClassSuffix>;
    @Input() htmlButtonType: "button" | "submit" = "button";
    @Input() buttonText?: string = "";
    @Input() disabled: boolean = false;
    @Input() disabledReason: string; // Will display on hover if button is disabled
    @Input() isLoading: boolean = false;
    @Input() loadingText: string;
    @Input() ariaLabel: string;
    @Input() title: string = "";
    @Input() buttonClass?: string = "";
    @Input() onClick?: OnClick;
    @Input() onClickArgs?: Parameters<OnClick> = <Parameters<OnClick>>[];
    @Input() defaultShowError = false;
    @Input() errHandler?: (err: any) => void = (err) => {
        this.defaultShowError && err && S25Util.showError(err);
    };
    readonly ariaExpanded = input<boolean | undefined>(undefined);
    readonly ariaControls = input<string | undefined>(undefined);

    @Output() clicked = new EventEmitter<MouseEvent>();
    @Output() clickPromise = new EventEmitter<Promise<unknown>>();
    @Output() isLoadingChange = new EventEmitter<boolean>();

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isLoading) {
            if (changes.isLoading.currentValue) this.startLoading();
            else this.stopLoading();
        }
    }

    ngOnInit() {
        if (!this.ariaLabel && this.buttonText) {
            this.ariaLabel = this.buttonText;
        }
    }

    startLoading() {
        // We don't want the button's size to change when we change its contents,
        // so we need to set a fixed height and width here
        const width = this.elementRef.nativeElement.offsetWidth;
        const height = this.elementRef.nativeElement.offsetHeight;
        this.renderer.setStyle(this.elementRef.nativeElement, "width", `${width}px`);
        this.renderer.setStyle(this.elementRef.nativeElement, "height", `${height}px`);
    }

    stopLoading() {
        // In startLoading() we set a fixed height and width,
        // so we need to remove that here
        this.renderer.removeStyle(this.elementRef.nativeElement, "width");
        this.renderer.removeStyle(this.elementRef.nativeElement, "height");
    }

    clickHandler($event: MouseEvent) {
        this.clicked.emit($event);
        if (this.onClick) {
            this.initLoading();
            const clickPromise = (this.onClick(...this.onClickArgs) || Promise.resolve()).then(
                () => {
                    this.clickPromiseHandler(clickPromise);
                },
                (err) => {
                    this.errHandler(err);
                    this.clickPromiseHandler(clickPromise);
                },
            );
        }
    }

    clickPromiseHandler(clickPromise?: Promise<unknown>) {
        this.stopLoading();
        this.isLoading = false;
        this.isLoadingChange.emit(this.isLoading);
        clickPromise && this.clickPromise.emit(clickPromise);
        this.cd.detectChanges();
    }

    initLoading() {
        this.startLoading();
        this.isLoading = true;
        this.isLoadingChange.emit(this.isLoading);
        this.cd.detectChanges();
    }
}
